import {
  QAlert,
  QBox,
  QButton,
  QLink,
  QSpinner,
  QStack,
  QText,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from 'react-query';
import { environment } from '../../../environments/environment';
import { CreateDevApiToken } from './CreateDevApiToken';
import { EditDevApiToken } from './EditDevApiToken';
import { NavList } from '../../../utils/umfe.enums';
import { useFlagProvider } from '../../../utils/flagProvider';
import { useMcpEnabled } from '../../../hooks/useMcpEnabled';
import { Header } from '../../../containers/header';
import { NoApiTokens } from './NoApiTokens';

import { listDevApiToken } from '../../../api/medtech-api';
import { ApiKey, CacheKeys } from '../../../types';
import { ApiTokensList } from './ApiTokensList';
import { createErrorToast } from '../../../utils/toast.utils';
import { UpsellDevApi } from './UpsellDevApi';
import { TokenModal } from './TokenModal';
import { DeleteApiTokenModal } from '../../modals/DeleteApiTokenModal';
import { RotateApiTokenModal } from '../../modals/RotateApiTokenModal';

const createTokenPath = '/admin/dev-api/token';
const editTokenPathPattern = /\/admin\/dev-api\/token\/(\d+)\/edit$/;

export const DevApi: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentUser = useCurrentUser();
  const { showToast } = useToastProvider();
  const { isFlagEnabled } = useFlagProvider();
  const editTokenEnabled = isFlagEnabled('editDeveloperApiToken');
  const showTokenTypeColumn = useMcpEnabled();
  const queryClient = useQueryClient();
  const [tokenValue, setTokenValue] = useState<string>();
  const [tokenToDelete, setTokenToDelete] = useState<ApiKey | null>(null);
  const [tokenToRotate, setTokenToRotate] = useState<ApiKey | null>(null);

  const { isLoading, data, isError } = useQuery([CacheKeys.API_KEYS, currentUser.companyId], () =>
    listDevApiToken(currentUser.companyId),
  );

  const onDelete = (item: ApiKey) => {
    setTokenToDelete(item);
  };

  const onRotate = (item: ApiKey) => {
    setTokenToRotate(item);
  };

  const goToCreate = async () => {
    navigate(createTokenPath);
  };

  const onEdit = editTokenEnabled ? (item: ApiKey) => navigate(`${createTokenPath}/${item.id}/edit`) : undefined;

  const handleDevPortalClick = async () => {
    const newWindow = window.open(environment.devPortal, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  if (isError) {
    showToast(createErrorToast(t('messages.error.showApiKeys'), 'dev-api-fetch-keys-error-toast'));
    return <div />;
  }

  if (isLoading || data === undefined) {
    return <QSpinner size="sm" />;
  }

  const editMatch = editTokenEnabled && pathname.match(editTokenPathPattern);
  if (editMatch) {
    return <EditDevApiToken tokenId={Number(editMatch[1])} />;
  }

  if (pathname.includes(createTokenPath)) {
    return <CreateDevApiToken />;
  }

  return (
    <UpsellDevApi>
      <QStack spacing={10}>
        <QStack direction="row" spacing={12} justify="space-between">
          <div>
            <Header content={t(NavList.DEV_API.title)} />
            <QText>{t('developerApi.description')}</QText>
            <QText marginTop={1}>{t('developerApi.manageDescription')}</QText>
            <QText marginTop={1}>{t('developerApi.expirationNote')}</QText>
          </div>
          {!!data.length && (
            <QBox w="120px">
              <QButton data-cy="create-token-button" onClick={goToCreate}>
                {t('developerApi.createToken')}
              </QButton>
            </QBox>
          )}
        </QStack>
        <QAlert
          status="info"
          data-cy="dev-docs-alert"
          description={
            <QText fontSize="sm">
              {t('developerApi.docsAlert')}{' '}
              <QLink href={environment.devPortal} isExternal>
                {t('developerApi.viewApiDocs')}
              </QLink>
            </QText>
          }
        />
        {tokenValue && <TokenModal token={tokenValue} onClose={() => setTokenValue(undefined)} />}
        {tokenToDelete && (
          <DeleteApiTokenModal
            closeModal={() => setTokenToDelete(null)}
            token={tokenToDelete}
            companyId={currentUser.companyId}
            onDeleted={() => queryClient.invalidateQueries({ queryKey: [CacheKeys.API_KEYS, currentUser.companyId] })}
          />
        )}
        {tokenToRotate && (
          <RotateApiTokenModal
            closeModal={() => setTokenToRotate(null)}
            token={tokenToRotate}
            companyId={currentUser.companyId}
            onRotated={(newToken) => {
              queryClient.invalidateQueries({ queryKey: [CacheKeys.API_KEYS, currentUser.companyId] });
              setTokenValue(newToken);
            }}
          />
        )}
        {!!data.length && (
          <ApiTokensList
            tokens={data}
            onDelete={onDelete}
            onEdit={onEdit}
            onRotate={onRotate}
            showTokenTypeColumn={showTokenTypeColumn}
          />
        )}
        {!data.length && <NoApiTokens handleDevPortalClick={handleDevPortalClick} goToCreate={goToCreate} />}
      </QStack>
    </UpsellDevApi>
  );
};
